/**
 * Fachada de notificaciones sobre Sileo (https://github.com/hiaaryan/sileo).
 *
 * Los 42 archivos que notifican llaman a `appToast.error(msg)` y compañía; esta
 * fachada existe para que cambiar de biblioteca no obligue a tocarlos. Antes
 * envolvía a Sonner — la API de entrada no cambió.
 *
 * Sileo tiene un modelo distinto al de Sonner, y estas tres diferencias son las
 * que condicionan todo lo de abajo:
 *
 * 1. **Título corto + cuerpo expandible, no un mensaje.** El título vive en una
 *    píldora de una línea (`white-space: nowrap` dentro de un contenedor con
 *    `overflow: hidden`, de 350px máximo); un mensaje largo ahí se recorta. Los
 *    mensajes del producto van de "Cliente creado" (14 caracteres) a oraciones
 *    de `getErrorMessage` de más de 100. Ver `present`.
 * 2. **Todo toast sin `id` comparte el id `"sileo-default"` y REEMPLAZA al
 *    anterior en lugar de apilarse.** Dos errores seguidos y el primero se
 *    pierde sin rastro. Sonner apila, así que aquí cada toast lleva su propio
 *    id — sin esto la migración sería una regresión silenciosa.
 * 3. **El colapso automático del cuerpo es una constante (4s), no relativa a la
 *    duración del toast.** Un error de 6s perdía su texto a los 4s y dejaba 2s
 *    de píldora con solo el titular. Ver `autopilot` en `present`.
 *
 * Duraciones calibradas por tipo (sin cambios respecto a la versión Sonner):
 *   error   — 6 000 ms: hay que leerlo y actuar
 *   warning — 5 000 ms: importante pero no bloquea
 *   info    — 4 000 ms: neutral
 *   success — 3 000 ms: confirmación breve, el usuario ya sabe qué pasó
 */
import { type SileoOptions, sileo } from 'sileo'

type Kind = 'success' | 'info' | 'warning' | 'error'

/**
 * `id` lo respeta el runtime de Sileo (v0.1.5, `createToast`: `merged.id`) pero
 * NO figura en `SileoOptions`. Es un campo no documentado, por eso `sileo` está
 * fijado a una versión exacta en `package.json` y este tipo es el ÚNICO sitio
 * donde se declara. Si una versión futura lo renombra, falla aquí y en ningún
 * otro lado.
 */
type WithId = SileoOptions & { readonly id: string }

const KIND: Record<Kind, { readonly duration: number; readonly headline: string }> = {
  error: { duration: 6_000, headline: 'No se pudo completar' },
  warning: { duration: 5_000, headline: 'Atención' },
  info: { duration: 4_000, headline: 'Aviso' },
  success: { duration: 3_000, headline: 'Listo' },
}

/**
 * Capacidad real del título dentro de la píldora: 350px de `WIDTH` fijo en el JS
 * de Sileo, menos 16 de padding, 24 del badge y 8 de separación = 302px; se usa
 * 288 para dejar ~5% de margen, porque equivocarse hacia el lado malo recorta el
 * final de un mensaje.
 *
 * **Se mide, no se cuentan caracteres.** Con la fuente real, "Número de teléfono
 * actualizado correctamente" (44 caracteres) ocupa 280px y cabe, mientras que un
 * texto de 50 caracteres ocupa 305px y no: el ancho por carácter varía entre
 * mensajes, así que un tope de caracteres o recorta mensajes válidos o deja
 * pasar uno que se corta.
 *
 * `TITLE_FONT` espeja el estilo de `[data-sileo-title]` (13.2px, peso 500, la
 * fuente de la app). Si Sileo cambia su tipografía, hay que cambiarlo aquí.
 */
const TITLE_FONT = '500 13.2px "Roboto Flex", ui-sans-serif, system-ui, sans-serif'
const TITLE_MAX_PX = 288

/** Respaldo cuando no hay canvas (entornos sin DOM): conservador a propósito. */
const TITLE_FALLBACK_CHARS = 36

let ruler: CanvasRenderingContext2D | null = null

function titleFits(text: string): boolean {
  if (text === '') return false
  if (typeof document === 'undefined') return text.length <= TITLE_FALLBACK_CHARS
  ruler ??= document.createElement('canvas').getContext('2d')
  if (ruler === null) return text.length <= TITLE_FALLBACK_CHARS
  ruler.font = TITLE_FONT
  return ruler.measureText(text).width <= TITLE_MAX_PX
}

/** Retraso del auto-expandido del cuerpo; es el default de Sileo (150ms). */
const AUTO_EXPAND_MS = 150

let counter = 0
const nextId = (): string => `app-toast-${++counter}`

/**
 * Presenta un toast. Si el mensaje cabe en la píldora, ES el título y el toast
 * se queda compacto. Si no cabe, el título pasa a ser el titular del tipo y el
 * mensaje completo va al cuerpo, que se expande solo.
 */
function present(kind: Kind, message: string, description: string | undefined, id: string): string {
  const { duration, headline } = KIND[kind]
  const text = message.trim()
  const fits = titleFits(text)

  // Si hay mensaje largo Y descripción explícita, van juntos: descartar
  // cualquiera de los dos perdería contenido sin avisar.
  const body = [fits ? null : text || null, description ?? null].filter(Boolean).join(' · ')

  const options: WithId = {
    id,
    title: fits ? text : headline,
    duration,
    ...(body !== ''
      ? {
          description: body,
          // El cuerpo ES el mensaje: no se pliega antes de que el toast salga.
          autopilot: { expand: AUTO_EXPAND_MS, collapse: duration },
        }
      : {}),
  }

  return sileo[kind](options)
}

export const appToast = {
  error: (message: string, description?: string): string =>
    present('error', message, description, nextId()),

  warning: (message: string): string => present('warning', message, undefined, nextId()),

  success: (message: string, description?: string): string =>
    present('success', message, description, nextId()),

  info: (message: string): string => present('info', message, undefined, nextId()),

  /**
   * Ciclo `cargando → éxito | error` sobre UN solo toast. Un `success`/`error`
   * posterior con el mismo `id` reemplaza al de carga en sitio — es el
   * "morph" característico de Sileo, y evita que queden dos toasts a la vez.
   *
   * El mensaje de `loading` va siempre en el título (no hay cuerpo): mantenerlo
   * corto, debe caber en la píldora (ver `titleFits`).
   */
  track: (id: string) => ({
    loading: (message: string): string => {
      const options: WithId = { id, title: message, type: 'loading', duration: null }
      return sileo.show(options)
    },
    success: (message: string, description?: string): string =>
      present('success', message, description, id),
    error: (message: string): string => present('error', message, undefined, id),
  }),
} as const
